import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

/**
 * "An account with {email} already exists — [Log in] or [Create a separate account]."
 *
 * Reached ONLY after the user has authenticated as this email (Google-verified, or email-code
 * verified) with SIGNUP intent and an email that already has ≥1 account. Because it is behind
 * proof-of-ownership, showing the email here is not an enumeration oracle.
 *
 * The user is already logged in when this renders, so:
 *  - Log in → route by account count, the shared rule (0→/onboarding, 1→/, >1→/lobby). One account
 *    is selected first so the console has one.
 *  - Create a separate account → /onboarding → /api/signup makes a NEW Account under the SAME User.
 */
const AccountExistsPage: React.FC = () => {
    const { user, accountId, selectAccount } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const { t } = useTranslation('common');

    // Not authenticated → nothing to disambiguate. Never render the "exists" copy to a stranger.
    if (!user) return <Navigate to="/login" replace />;
    // Already inside an account (e.g. refresh after choosing) → go home.
    if (accountId) return <Navigate to="/" replace />;

    const email = user.email;
    const count = user.accounts?.length ?? 0;

    const logIn = async () => {
        setBusy(true);
        // Mirror of destinationForAccountCount (authDestination.ts) on the client. Kept trivial and
        // covered by the e2e path; the server owns the canonical rule for its own redirects.
        if (count === 0) { navigate('/onboarding'); return; }
        if (count === 1) {
            await selectAccount(user.accounts[0].accountId);
            navigate('/');
            return;
        }
        navigate('/lobby');
    };

    const createSeparate = () => {
        // /onboarding → OnboardingWizard → /api/signup creates a NEW account under this same user.
        navigate('/onboarding');
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background-light dark:bg-background-dark px-6">
            <div className="w-full max-w-md flex flex-col gap-6 rounded-2xl border border-[#d1dbe5] dark:border-[#31465c] bg-white dark:bg-[#1e2b37] p-8 shadow-sm">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-bold text-[#0d141b] dark:text-white">{t('auth.accountExists.title')}</h1>
                    <p className="text-sm text-[#4c739a] dark:text-slate-400">
                        <Trans
                            i18nKey="auth.accountExists.body"
                            values={{ email }}
                            components={{ strong: <span className="font-semibold text-[#0d141b] dark:text-white" /> }}
                        />
                    </p>
                </div>

                <button
                    onClick={logIn}
                    disabled={busy}
                    className="flex h-12 w-full items-center justify-center rounded-lg bg-primary px-4 text-base font-bold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                    {t('auth.accountExists.logIn')}
                </button>

                <div className="flex items-center gap-3" aria-hidden="true">
                    <div className="h-px flex-1 bg-[#d1dbe5] dark:bg-[#31465c]" />
                    <span className="text-xs font-medium uppercase tracking-wider text-[#4c739a]">{t('auth.accountExists.or')}</span>
                    <div className="h-px flex-1 bg-[#d1dbe5] dark:bg-[#31465c]" />
                </div>

                <button
                    onClick={createSeparate}
                    disabled={busy}
                    className="flex h-12 w-full items-center justify-center rounded-lg border border-primary/40 bg-primary/5 px-4 text-base font-bold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                    {t('auth.accountExists.createSeparate')}
                </button>
                <p className="text-xs text-[#4c739a] dark:text-slate-400 text-center">
                    {t('auth.accountExists.separateHint')}
                </p>
            </div>
        </div>
    );
};

export default AccountExistsPage;
